import React from "react";
import { Chart } from "react-chartjs-2";

import { BarElement, CategoryScale, ChartData, Chart as ChartJS, ChartOptions, Legend, LineElement, LinearScale, PointElement, Tooltip } from "chart.js";

import { formatNumber, useTranslation } from "utils";

ChartJS.register(BarElement, CategoryScale, LinearScale, LineElement, PointElement, Legend, Tooltip);

type PayoutBaselineBarChartProps = {
  years: number[];
  values: number[];
  average: number;
};

export function PayoutBaselineBarChart({ years, values, average }: PayoutBaselineBarChartProps) {
  const { t } = useTranslation("fieldTimeline");
  const maxValue = Math.max(0, ...values, average);
  const minValue = Math.min(0, ...values, average);
  const safeSpan = Math.max(Math.abs(maxValue), Math.abs(minValue), 50);
  const rangePadding = safeSpan * 0.1;
  const yMax = Math.ceil((maxValue + rangePadding) / 50) * 50;
  const yMin = Math.floor((minValue - rangePadding) / 50) * 50;

  const data: ChartData<"bar" | "line"> = {
    labels: years.map(String),
    datasets: [
      {
        type: "bar",
        label: "positive",
        data: values.map((value) => (value >= 0 ? value : Number.NaN)),
        backgroundColor: "#d9d9d9",
        borderRadius: 6,
        borderSkipped: false,
        barThickness: 64,
      },
      {
        type: "bar",
        label: "negative",
        data: values.map((value) => (value < 0 ? value : Number.NaN)),
        backgroundColor: "#efb6a7",
        borderRadius: 6,
        borderSkipped: false,
        barThickness: 64,
      },
      {
        type: "line",
        label: "average",
        data: years.map(() => average),
        borderColor: "#0d7058",
        borderWidth: 1.5,
        borderDash: [3, 3],
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 20,
      },
    ],
  };

  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        displayColors: false,
        filter: (item) => item.dataset.label !== "zeroAxis",
        callbacks: {
          title: (items) => `${items[0]?.label ?? ""}`,
          label: (item) => {
            const value = item.parsed.y ?? 0;
            if (item.dataset.type === "line") return t("impactPanel.payoutImpact.contextDrawer.baseline.tooltipBaselineAverage", { value: Math.round(value) });
            if (item.datasetIndex === 0) return t("impactPanel.payoutImpact.contextDrawer.baseline.tooltipPositiveImpact", { value: Math.round(value) });
            return t("impactPanel.payoutImpact.contextDrawer.baseline.tooltipNegativeYear", { value: Math.round(value) });
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: "#6d6d6d",
          font: {
            family: "dazzed",
            size: 12,
            weight: 500,
          },
          padding: 8,
        },
      },
      y: {
        min: yMin,
        max: yMax,
        grid: {
          color: "#d9d9d9",
          drawTicks: false,
          lineWidth: 1,
        },
        border: {
          display: false,
        },
        ticks: {
          display: true,
          stepSize: 50,
          color: "#6d6d6d",
          callback: (value) => `${formatNumber({ number: Number(value), decimalPlaces: 0 })}t`,
          font: {
            family: "dazzed",
            size: 12,
            weight: 500,
          },
          padding: 8,
        },
      },
    },
  };

  const overlayLinesPlugin = {
    id: "baselineOverlayLines",
    afterDatasetsDraw(chart: ChartJS<"bar" | "line">) {
      const { ctx, chartArea, scales } = chart;
      const yScale = scales.y;
      if (!yScale) return;

      const averageY = yScale.getPixelForValue(average);
      const zeroY = yScale.getPixelForValue(0);

      ctx.save();

      // Draw zero separator over bars.
      ctx.strokeStyle = "#6d6d6d";
      ctx.lineWidth = 1.25;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, zeroY);
      ctx.lineTo(chartArea.right, zeroY);
      ctx.stroke();

      // Draw average dashed line over bars.
      ctx.strokeStyle = "#0d7058";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, averageY);
      ctx.lineTo(chartArea.right, averageY);
      ctx.stroke();

      ctx.restore();
    },
  };

  return <Chart type="bar" data={data} options={options} plugins={[overlayLinesPlugin]} data-testid="payout-baseline-chart" />;
}
